import { useGetGroup } from './useGetGroup';
import { useParams } from 'react-router-dom';
import {
  GroupDto,
  GroupsApi,
  GroupsV2Api,
  ResourceDto,
  RoleBindingDto,
  RolesApi,
  UserDto,
} from '../../../api-client';
import { getApiConfiguration } from '../../../common';
import { useGetGroupUsers } from './useGetGroupUsers';
import { useGetGroupRoles } from './useGetGroupRoles';
import { ErrorResponse } from '../../../common/types/errorRespose';
import { QueryObserverResult } from 'react-query';
import { useGetCustomRoles } from '../../CustomRoles/hooks/useGetCustomRoles';
import { useGetBuiltInRoles } from '../../BuiltInRoles/hooks/useGetBuiltInRoles';
import { useContext } from 'react';
import { NotificationsContext } from '../../../common/notifications/Notifications';
import { useGetBusinessUnits } from './useGetBusinessUnits';
import { CombinedRoles } from '../GroupDetails.types';

export const useGroupDetailsQueries = (
  groupsApi: GroupsApi,
  groupsV2Api: GroupsV2Api,
): {
  group: GroupDto | undefined;
  groupUsers: UserDto[] | undefined;
  groupRoles: RoleBindingDto[] | undefined;
  roles: CombinedRoles | undefined;
  businessUnits: ResourceDto[] | undefined;
  isFetching: boolean;
  fetchError: ErrorResponse | null;
  refetchGroupUsers: () => Promise<QueryObserverResult<UserDto[]>>;
  refetchGroupRoles: () => Promise<QueryObserverResult<RoleBindingDto[]>>;
} => {
  const { id } = useParams<{ id: string }>();
  const rolesApi = new RolesApi(getApiConfiguration());
  const { showNotification } = useContext(NotificationsContext);

  const {
    data: group,
    isFetching: isFetchingGroup,
    error: fetchGroupError,
  } = useGetGroup(groupsApi, id, {
    retry: false,
  });

  const {
    data: groupUsers,
    isFetching: isFetchingGroupUsers,
    error: fetchGroupUsersError,
    refetch: refetchGroupUsers,
  } = useGetGroupUsers(groupsApi, id, {
    retry: false,
  });

  const {
    data: groupRoles,
    isFetching: isFetchingGroupRoles,
    error: fetchGroupRolesError,
    refetch: refetchGroupRoles,
  } = useGetGroupRoles(groupsV2Api, id, {
    retry: false,
  });

  const {
    data: customRoles,
    isFetching: isFetchingCustomRoles,
    error: fetchCustomRolesError,
  } = useGetCustomRoles({
    retry: false,
  });

  const {
    data: builtInRoles,
    isFetching: isFetchingBuiltInRoles,
    error: fetchBuiltInRolesError,
  } = useGetBuiltInRoles(rolesApi, () => {
    showNotification('An error occurred while fetching built-in roles');
  });

  const { data: businessUnits } = useGetBusinessUnits({
    retry: false,
  });

  return {
    group,
    groupUsers,
    groupRoles,
    businessUnits,
    roles:
      customRoles &&
      builtInRoles &&
      ([
        ...customRoles.map((r) => ({ ...r, isCustom: true })),
        ...builtInRoles.map((r) => ({ ...r, isCustom: false })),
      ] as CombinedRoles),
    isFetching:
      isFetchingGroup ||
      isFetchingGroupUsers ||
      isFetchingGroupRoles ||
      isFetchingCustomRoles ||
      isFetchingBuiltInRoles,
    fetchError: (fetchGroupError ||
      fetchGroupUsersError ||
      fetchGroupRolesError ||
      fetchCustomRolesError ||
      fetchBuiltInRolesError) as ErrorResponse | null,
    refetchGroupUsers,
    refetchGroupRoles,
  };
};
